import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Container,
  IconButton,
  Tooltip,
} from '@mui/material';
import { getSchemeIcon } from '@/lib/schemeIcons';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { getRuntimeEnv, isDevMode } from '@/lib/runtimeEnv';
import { toast } from '@/lib/toast';
import ThemeModeToggle from '@/components/ThemeModeToggle';
import SchemePickerDialog from '@/components/SchemePickerDialog';

// Demo affordances only exist in dev mode; the creds panel additionally
// wants its own opt-in flag.
const DEV_MODE = isDevMode();
const SHOW_DEMO_CREDS =
  DEV_MODE && getRuntimeEnv('REACT_APP_SHOW_DEMO_CREDS', 'false') === 'true';

const DEMO_CREDS = [
  { email: 'admin@noricrm.io', password: 'admin123', color: 'primary.main' },
  { email: 'trainer@noricrm.io', password: 'trainer123', color: 'secondary.main' },
  { email: 'member@noricrm.io', password: 'member123', color: 'warning.main' },
];

const lightGradient = 'linear-gradient(135deg, #eef2f7 0%, #d6e0ee 50%, #c3cfe2 100%)';
const darkGradient = 'linear-gradient(135deg, #050505 0%, #101418 100%)';

const buildBackground = (mode, branding) => {
  const url = mode === 'light' ? branding.login_bg_light_url : branding.login_bg_dark_url;
  if (url) {
    const overlay =
      mode === 'light'
        ? 'rgba(245, 246, 248, 0.78)'
        : 'rgba(10, 10, 10, 0.88)';
    return `linear-gradient(${overlay}, ${overlay}), url(${url})`;
  }
  return mode === 'light' ? lightGradient : darkGradient;
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [schemePickerOpen, setSchemePickerOpen] = useState(false);
  const { login } = useAuth();
  const { branding, mode } = useBranding();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      toast.success('Welcome back!');
      navigate('/dashboard');
    } else {
      toast.error(result.error || 'Login failed');
    }
    setLoading(false);
  };

  const logoUrl = mode === 'dark' ? branding.logo_dark_url || branding.logo_url : branding.logo_url;
  const SchemeIcon = getSchemeIcon(branding?.scheme);

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        backgroundImage: buildBackground(mode, branding),
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 2 }}>
        <ThemeModeToggle />
      </Box>

      <Container maxWidth="sm">
        <Paper
          elevation={8}
          sx={{ p: 4, bgcolor: 'background.paper', border: 1, borderColor: 'divider', position: 'relative' }}
        >
          {DEV_MODE && (
            <Tooltip title="Switch demo (Gym / Barber / Spa)">
              <IconButton
                size="small"
                onClick={() => setSchemePickerOpen(true)}
                aria-label="Switch demo tenant"
                data-testid="scheme-picker-trigger"
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: 12,
                  color: 'primary.main',
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <SchemeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, minHeight: 48 }}>
            {logoUrl ? (
              <Box
                component="img"
                src={logoUrl}
                alt={`${branding.app_name} logo`}
                sx={{ maxHeight: 64, maxWidth: '70%', objectFit: 'contain' }}
              />
            ) : (
              <SchemeIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            )}
          </Box>
          <Typography variant="h4" align="center" color="primary" gutterBottom>
            {branding.app_name}
          </Typography>
          {branding.tagline && (
            <Typography align="center" color="text.secondary" variant="body2" sx={{ mb: 4 }}>
              {branding.tagline}
            </Typography>
          )}

          <form onSubmit={handleSubmit} data-testid="login-form">
            <TextField
              id="email"
              label="Email"
              type="email"
              fullWidth
              required
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              data-testid="login-email"
              sx={{ '& .MuiInputBase-input': { py: 1.75 } }}
            />
            <TextField
              id="password"
              label="Password"
              type="password"
              fullWidth
              required
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              data-testid="login-password"
              sx={{ '& .MuiInputBase-input': { py: 1.75 } }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              disabled={loading}
              data-testid="login-submit"
              sx={{ mt: 3, py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </form>

          {SHOW_DEMO_CREDS && (
            <Paper
              variant="outlined"
              sx={{
                mt: 3,
                p: 2,
                bgcolor: 'action.hover',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Demo Accounts
              </Typography>
              {DEMO_CREDS.map((c) => (
                <Typography
                  key={c.email}
                  variant="caption"
                  component="div"
                  sx={{ display: 'block' }}
                  color={c.color}
                >
                  {c.email} / {c.password}
                </Typography>
              ))}
            </Paper>
          )}
        </Paper>
      </Container>

      {DEV_MODE && (
        <SchemePickerDialog open={schemePickerOpen} onClose={() => setSchemePickerOpen(false)} />
      )}
    </Box>
  );
};

export default LoginPage;
